import { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Tabs,
  Spin,
  Progress,
  List,
  Typography,
  Collapse,
  Avatar,
  Tooltip,
} from "antd";
import {
  WarningOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  SolutionOutlined,
  BankOutlined,
  ShopOutlined,
  CarOutlined,
  TeamOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { FetchAgingStock_api, FetchDashboardSummary_api } from "../../Api";
import { LoadingScreen } from "../../components";

const { TabPane } = Tabs;
const { Text } = Typography;
const { Panel } = Collapse;

export const Dashboard = () => {
  // Fetch Stock Aging
  const { data: agingStock, isLoading: isLoadingAging } = useQuery({
    queryKey: ["agingStock"],
    queryFn: FetchAgingStock_api,
    refetchOnWindowFocus: false,
  });

  // Fetch Summary Metrics
  const { data: summaryMetrics, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ["dashboardSummary"],
    queryFn: FetchDashboardSummary_api,
    refetchOnWindowFocus: false,
  });

  const agingData = agingStock?.agingData || {
    critical: [],
    warning: [],
    normal: [],
  };
  const stockSummary = agingStock?.summary || {
    critical: 0,
    warning: 0,
    normal: 0,
  };
  const metrics = summaryMetrics?.dashboardData || {};
  // stockByModel Structure:
  // Main Stock: [{ vehicle_model: 'CT100', count: 5 }, ...]
  // Dealer Stock: { 'Dealer A': [{ model: 'CT100', count: 2 }], 'Dealer B': ... }
  const stockByModel = metrics?.stockByModel || {
    "Main Stock": [],
    "Dealer Stock": {},
  };

  const isLoading = isLoadingAging || isLoadingMetrics;

  const columns = [
    {
      title: "Vehicle Info",
      key: "vehicle",
      render: (text: any, record: any) => (
        <div>
          <span className="font-bold">
            {record.v_type || "-"} - {record.v_modal || "-"}
          </span>
          <div className="text-xs text-gray-500">
            ENG: {record.engine_number} <br />
            CHAS: {record.chassis_number}
          </div>
        </div>
      ),
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
    },
    {
      title: "Added Date",
      dataIndex: "added_date",
      key: "added_date",
    },
    {
      title: "In Stock (Days)",
      dataIndex: "age",
      key: "age",
      sorter: (a: any, b: any) => a.age - b.age,
      render: (age: number) => {
        let color = "green";
        if (age > 7) color = "red";
        else if (age >= 4) color = "orange";
        return (
          <Tag color={color} className="font-bold text-base">
            {age} Days
          </Tag>
        );
      },
    },
    {
      title: "Dealer",
      dataIndex: "dealer_name",
      key: "dealer_name",
    },
  ];

  const safeArray = (val: any) => (Array.isArray(val) ? val : []);

  const safePercent = (count: number, total: number) =>
    total > 0 ? Math.round((count / total) * 100) : 0;

  if (isLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  const metricCardStyle = "shadow-sm border-l-4";

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Operational Dashboard
          </h1>
          <p className="text-gray-500">
            Overview of vehicle stock and inquiries
          </p>
        </div>
      </div>

      {/* Financials Row */}
      <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
        Financial Overview
      </h2>
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            className={`${metricCardStyle} border-blue-500`}
          >
            <Statistic
              title={
                <div className="flex items-center gap-1">
                  Total Revenue (Paid)
                  <Tooltip title="Sum of all 'Paid' inquiries for vehicles officially marked as 'Sold' (Bikes & Three Wheels only).">
                    <InfoCircleOutlined className="text-gray-400 text-xs cursor-help" />
                  </Tooltip>
                </div>
              }
              value={metrics.totalRevenue}
              precision={2}
              prefix="Rs "
              valueStyle={{ color: "#1890ff", fontSize: "1.2rem" }}
            />
            <div className="text-xs text-gray-500 mt-2">
              <DollarOutlined /> Confirmed Sales
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            className={`${metricCardStyle} border-purple-500`}
          >
            <Statistic
              title={
                <div className="flex items-center gap-1">
                  Internal Account Balance
                  <Tooltip title="Total cash currently available in company owned bank accounts.">
                    <InfoCircleOutlined className="text-gray-400 text-xs cursor-help" />
                  </Tooltip>
                </div>
              }
              value={metrics.totalAccountBalance}
              precision={2}
              prefix="Rs "
              valueStyle={{ color: "#722ed1", fontSize: "1.2rem" }}
            />
            <div className="text-xs text-gray-500 mt-2">
              <BankOutlined /> Company Liquidity
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            className={`${metricCardStyle} border-orange-500`}
          >
            <div className="flex justify-between items-start">
              <Statistic
                title={
                  <div className="flex items-center gap-1">
                    Pending Inquiries
                    <Tooltip title="Count and estimated value of inquiries currently in 'Pending' status for Bikes & Three Wheels.">
                      <InfoCircleOutlined className="text-gray-400 text-xs cursor-help" />
                    </Tooltip>
                  </div>
                }
                value={metrics.pendingInquiries}
                prefix={<SolutionOutlined />}
                valueStyle={{ color: "#fa8c16" }}
              />
              <div className="text-right">
                <span className="text-xs text-gray-400">Est. Value</span>
                <div className="text-sm font-semibold text-gray-600">
                  Rs{" "}
                  {metrics.pendingValue?.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Awaiting Customer Payment
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            className={`${metricCardStyle} border-red-500`}
          >
            <div className="flex justify-between items-start">
              <Statistic
                title={
                  <div className="flex items-center gap-1">
                    Company Obligations
                    <Tooltip title="Total amount owed to the main company for 'Paid' inquiries where 'isPaidCompany' is not yet checked.">
                      <InfoCircleOutlined className="text-gray-400 text-xs cursor-help" />
                    </Tooltip>
                  </div>
                }
                value={metrics.pendingCompanyPayments}
                prefix={<ShopOutlined />}
                valueStyle={{ color: "#f5222d" }}
              />
              <div className="text-right">
                <span className="text-xs text-gray-400">Gross Value</span>
                <div className="text-sm font-semibold text-gray-600">
                  Rs{" "}
                  {metrics.pendingCompanyValue?.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Unpaid to Main Company
            </div>
          </Card>
        </Col>
      </Row>

      {/* Inventory & Operations */}
      <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
        Inventory & Operations
      </h2>
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            className={`${metricCardStyle} border-cyan-500`}
          >
            <Statistic
              title={
                <div className="flex items-center gap-1">
                  Main Stock
                  <Tooltip title="Total available Bikes & Three Wheels currently in the main warehouse.">
                    <InfoCircleOutlined className="text-gray-400 text-xs cursor-help" />
                  </Tooltip>
                </div>
              }
              value={metrics.totalStock}
              prefix={<CarOutlined />}
              valueStyle={{ color: "#13c2c2" }}
            />
            <div className="text-xs text-gray-500 mt-2">Available</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            className={`${metricCardStyle} border-blue-400`}
          >
            <Statistic
              title={
                <div className="flex items-center gap-1">
                  Dealer Stock
                  <Tooltip title="Total available Bikes & Three Wheels currently located at various dealer shops.">
                    <InfoCircleOutlined className="text-gray-400 text-xs cursor-help" />
                  </Tooltip>
                </div>
              }
              value={metrics.totalDealerStock}
              prefix={<ShopOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
            <div className="text-xs text-gray-500 mt-2">At Dealers</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            className={`${metricCardStyle} border-green-600`}
          >
            <Statistic
              title={
                <div className="flex items-center gap-1">
                  Total Sold
                  <Tooltip title="Total number of Bikes & Three Wheels officially marked as 'Sold' in the history.">
                    <InfoCircleOutlined className="text-gray-400 text-xs cursor-help" />
                  </Tooltip>
                </div>
              }
              value={metrics.totalSold}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
            <div className="text-xs text-gray-500 mt-2">Historical</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            className={`${metricCardStyle} border-indigo-600`}
          >
            <Statistic
              title={
                <div className="flex items-center gap-1">
                  Active Customers
                  <Tooltip title="Total unique customers registered in the system database.">
                    <InfoCircleOutlined className="text-gray-400 text-xs cursor-help" />
                  </Tooltip>
                </div>
              }
              value={metrics.totalCustomers}
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#2f54eb" }}
            />
            <div className="text-xs text-gray-500 mt-2">Total Database</div>
          </Card>
        </Col>
      </Row>

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold mb-4 text-gray-700 mt-8 border-b pb-2">
            Stock Aging Analysis
          </h2>

          <Row gutter={[24, 24]} className="mb-6 flex flex-wrap">
            <Col xs={24} md={8}>
              <Card
                bordered={false}
                className="bg-red-50 border-l-4 border-red-500 shadow-sm"
              >
                <Statistic
                  title={
                    <span className="text-red-600 font-semibold">
                      Critical Stock (&gt; 7 Days)
                    </span>
                  }
                  value={stockSummary.critical}
                  prefix={<AlertOutlined />}
                  valueStyle={{ color: "#cf1322" }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                bordered={false}
                className="bg-orange-50 border-l-4 border-orange-500 shadow-sm"
              >
                <Statistic
                  title={
                    <span className="text-orange-600 font-semibold">
                      Warning Stock (4-7 Days)
                    </span>
                  }
                  value={stockSummary.warning}
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: "#d46b08" }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                bordered={false}
                className="bg-green-50 border-l-4 border-green-500 shadow-sm"
              >
                <Statistic
                  title={
                    <span className="text-green-600 font-semibold">
                      Fresh Stock (&lt; 4 Days)
                    </span>
                  }
                  value={stockSummary.normal}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: "#3f8600" }}
                />
              </Card>
            </Col>
          </Row>
        </div>
        <div></div>
      </div>

      <Card className="shadow-md border-gray-100">
        <Tabs defaultActiveKey="1" type="card">
          <TabPane
            tab={
              <span className="text-red-500 font-bold">
                Critical List ({stockSummary.critical})
              </span>
            }
            key="1"
          >
            <Table
              className="overflow-x-auto"
              dataSource={agingData.critical}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
          <TabPane
            tab={
              <span className="text-orange-500 font-bold">
                Warning List ({stockSummary.warning})
              </span>
            }
            key="2"
          >
            <Table
              className="overflow-x-auto"
              dataSource={agingData.warning}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
          <TabPane tab="Fresh Stock" key="3">
            <Table
              className="overflow-x-auto"
              dataSource={agingData.normal}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};
